import { useEffect, useMemo, useRef, useState } from "react";
import { AnswerField } from "./components/AnswerField";
import { BriefView } from "./components/BriefView";
import { Intro } from "./components/Intro";
import { generateBrief } from "./domain/brief";
import { getRouteEstimate, getVisibleQuestions } from "./domain/questions";
import type { SubmissionContent } from "./domain/canonical";
import {
  SCHEMA_VERSION,
  type AnswerValue,
  type Contact,
  type DiscoveryContext,
  type Draft,
  type Receipt,
} from "./domain/types";
import { ApiError, makeSubmissionRequest, retryOutbox, savePrivateDraft, startPrivateSession, submitOrQueue } from "./lib/api";
import { indexedDbAvailable, loadDraft, saveDraft } from "./lib/idb";
import { enterDiscovery, publicLandingReady } from "./lib/analytics";

type Screen = "questions" | "brief" | "send" | "receipt";
type SaveState = "saving" | "device" | "both" | "offline" | "unavailable";

const ACTIVE_DRAFT_KEY = "pd-discovery-active-draft";

function makeResumeToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function hasAnswer(value: AnswerValue | undefined): boolean {
  return Array.isArray(value) ? value.length > 0 : typeof value === "string" ? value.trim().length > 0 : typeof value === "boolean";
}

function routeName(context: DiscoveryContext): string {
  const vertical = {
    film: "Film",
    tv: "TV / series",
    advertising: "Advertising",
    business: "Business",
    general: "General / other",
  }[context.vertical];
  const service = {
    writing: "Writing",
    design: "Design",
    writing_design: "Writing + design",
  }[context.service];
  return `${vertical} · ${service}`;
}

function SaveIndicator({ state }: { state: SaveState }) {
  const content: Record<SaveState, string> = {
    saving: "Saving…",
    device: "Saved on this device",
    both: "Saved here + private copy",
    offline: "Offline · safe on this device",
    unavailable: "Local save unavailable · download a copy",
  };
  const needsAnnouncement = state === "offline" || state === "unavailable";
  return <div className="save-indicator" data-state={state} role={needsAnnouncement ? "status" : undefined}><span aria-hidden="true" />{content[state]}</div>;
}

function App() {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [screen, setScreen] = useState<Screen>("questions");
  const [questionIndex, setQuestionIndex] = useState(0);
  const [saveState, setSaveState] = useState<SaveState>(indexedDbAvailable() ? "device" : "unavailable");
  const [fieldError, setFieldError] = useState<string>();
  const [resumeNotice, setResumeNotice] = useState<string>();
  const [loadingResume, setLoadingResume] = useState(true);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const initialization = useRef(false);
  const activeDraftId = useRef<string | undefined>(undefined);
  const questionHeadingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    activeDraftId.current = draft?.draftId;
  }, [draft?.draftId]);

  useEffect(() => {
    if (initialization.current) return;
    initialization.current = true;
    void (async () => {
      const hashToken = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("resume");
      if (hashToken) {
        enterDiscovery();
        try {
          const remote = await startPrivateSession(hashToken);
          if (remote) {
            setDraft({ ...remote, resumeToken: hashToken });
            setQuestionIndex(remote.currentChapter ?? 0);
            setSaveState("both");
            localStorage.setItem(ACTIVE_DRAFT_KEY, remote.draftId);
            setResumeNotice("Private draft recovered.");
            setLoadingResume(false);
            return;
          }
        } catch {
          setResumeNotice("Private copy could not be reached. Local recovery still checked.");
        }
      }
      const activeId = localStorage.getItem(ACTIVE_DRAFT_KEY);
      if (activeId) enterDiscovery();
      if (activeId && indexedDbAvailable()) {
        try {
          const local = await loadDraft(activeId);
          if (local?.schemaVersion === SCHEMA_VERSION) {
            setDraft(local);
            setQuestionIndex(local.currentChapter ?? 0);
            setResumeNotice("Your last draft is back where you left it.");
          }
        } catch {
          setSaveState("unavailable");
        }
      }
      publicLandingReady();
      setLoadingResume(false);
    })();
  }, []);

  useEffect(() => {
    const handlePop = (event: PopStateEvent) => {
      if (typeof event.state?.questionIndex === "number") {
        setScreen("questions");
        setQuestionIndex(event.state.questionIndex);
      }
    };
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, []);

  useEffect(() => {
    const retry = () => {
      void retryOutbox().then((receipts) => {
        const latest = receipts.at(-1);
        if (latest && latest.submissionId === activeDraftId.current) {
          setReceipt(latest);
          showScreen("receipt");
        } else if (latest) {
          setResumeNotice(`Earlier submission ${latest.receiptId} reached pitch.dog. Its receipt remains saved on this device.`);
        }
      });
    };
    window.addEventListener("online", retry);
    return () => window.removeEventListener("online", retry);
  }, []);

  useEffect(() => {
    if (!draft) return;
    setSaveState("saving");
    const localTimer = window.setTimeout(() => {
      void saveDraft(draft)
        .then(() => setSaveState(draft.mode === "private" ? "device" : "device"))
        .catch(() => setSaveState("unavailable"));
    }, 250);
    const serverTimer = draft.mode === "private"
      ? window.setTimeout(() => {
          void savePrivateDraft(draft)
            .then(() => setSaveState("both"))
            .catch(() => setSaveState(navigator.onLine ? "device" : "offline"));
        }, 1_800)
      : undefined;
    return () => {
      window.clearTimeout(localTimer);
      if (serverTimer) window.clearTimeout(serverTimer);
    };
  }, [draft]);

  const visibleQuestions = useMemo(
    () => (draft ? getVisibleQuestions(draft, draft.answers) : []),
    [draft],
  );
  const boundedQuestionIndex = Math.min(questionIndex, Math.max(visibleQuestions.length - 1, 0));
  const currentQuestion = visibleQuestions[boundedQuestionIndex];
  const brief = useMemo(() => (draft ? generateBrief(draft, draft.answers) : null), [draft]);

  useEffect(() => {
    if (!draft) return;
    const timer = window.setTimeout(() => window.scrollTo({ top: 0, behavior: "auto" }), 0);
    return () => window.clearTimeout(timer);
  }, [draft?.draftId, screen]);

  useEffect(() => {
    if (screen !== "questions" || !currentQuestion) return;
    const timer = window.setTimeout(() => questionHeadingRef.current?.focus({ preventScroll: true }), 0);
    return () => window.clearTimeout(timer);
  }, [currentQuestion?.id, screen]);

  async function start(context: DiscoveryContext & { contact: Contact }) {
    enterDiscovery();
    const now = new Date().toISOString();
    const next: Draft = {
      ...context,
      draftId: crypto.randomUUID(),
      schemaVersion: SCHEMA_VERSION,
      answers: {},
      startedAt: now,
      updatedAt: now,
      currentChapter: 0,
    };
    if (context.mode === "private") {
      const token = makeResumeToken();
      next.resumeToken = token;
      try {
        await startPrivateSession(token);
        window.history.replaceState({ questionIndex: 0 }, "", `${window.location.pathname}#resume=${encodeURIComponent(token)}`);
        setSaveState("both");
      } catch {
        setResumeNotice("Private server copy is not connected yet. Work continues safely on this device.");
        setSaveState("device");
      }
    } else {
      window.history.replaceState({ questionIndex: 0 }, "", window.location.pathname);
    }
    localStorage.setItem(ACTIVE_DRAFT_KEY, next.draftId);
    setDraft(next);
    setQuestionIndex(0);
    showScreen("questions");
  }

  function setAnswer(value: AnswerValue) {
    if (!draft || !currentQuestion) return;
    setFieldError(undefined);
    setDraft({
      ...draft,
      answers: { ...draft.answers, [currentQuestion.id]: value },
      updatedAt: new Date().toISOString(),
      currentChapter: boundedQuestionIndex,
    });
  }

  function moveTo(index: number, push = true) {
    const next = Math.max(0, Math.min(index, visibleQuestions.length - 1));
    setQuestionIndex(next);
    setFieldError(undefined);
    setDraft((current) => current ? { ...current, currentChapter: next, updatedAt: new Date().toISOString() } : current);
    if (push) window.history.pushState({ questionIndex: next }, "", window.location.href);
    window.scrollTo({ top: 0, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  }

  function showScreen(next: Screen) {
    setScreen(next);
    window.scrollTo({ top: 0, behavior: "auto" });
  }

  function nextQuestion() {
    if (!draft || !currentQuestion) return;
    const value = draft.answers[currentQuestion.id];
    if (currentQuestion.required && !hasAnswer(value)) {
      setFieldError("Answer this, or choose “I don’t know yet”. Your work stays here.");
      return;
    }
    if (boundedQuestionIndex >= visibleQuestions.length - 1) {
      showScreen("brief");
      return;
    }
    moveTo(boundedQuestionIndex + 1);
  }

  function reset() {
    if (!window.confirm("Start a new route? Your current draft stays saved on this device, but it will no longer open automatically.")) return;
    localStorage.removeItem(ACTIVE_DRAFT_KEY);
    window.history.replaceState({}, "", window.location.pathname);
    setDraft(null);
    setQuestionIndex(0);
    setScreen("questions");
    setReceipt(null);
    setResumeNotice(undefined);
  }

  if (loadingResume) {
    return <main className="loading-screen" id="main"><p className="mono-label">PITCH.DOG · DECK DISCOVERY</p><h1>Finding your place…</h1></main>;
  }

  if (!draft) return <Intro onStart={start} />;
  if (!brief) return null;

  if (screen === "brief") {
    return <BriefView brief={brief} onReturn={() => showScreen("questions")} onSend={() => showScreen("send")} />;
  }

  if (screen === "receipt" && receipt) {
    const emailSubject = encodeURIComponent(`Deck discovery files · ${receipt.receiptId}`);
    return (
      <main className="receipt-screen" id="main">
        <div className="receipt-mark" aria-hidden="true">✓</div>
        <p className="mono-label">RECEIVED BY PITCH.DOG</p>
        <h1>Your answers are safe.</h1>
        <p className="receipt-intro">Two server copies hold the same response. We are also sending the full answer copy to <strong>{draft.contact.email}</strong>. This receipt is the proof—not the email notification.</p>
        <dl className="receipt-data">
          <div><dt>Receipt</dt><dd>{receipt.receiptId}</dd></div>
          <div><dt>Revision</dt><dd>{receipt.revision}</dd></div>
          <div><dt>Protected at</dt><dd>{new Date(receipt.receivedAt).toLocaleString()}</dd></div>
          <div><dt>Integrity</dt><dd>{receipt.payloadHash.slice(0, 16)}…</dd></div>
        </dl>
        <section className="upload-after">
          <p className="mono-label">FILES ARE A SEPARATE STEP</p>
          <h2>Your text submission is complete without them.</h2>
          <p>If you have scripts, images, decks, or references, email them with the receipt in the subject. A dedicated Google upload route will appear here only after its ownership and mobile sign-in are verified.</p>
          <a className="ink-button" href={`mailto:hello@pitch.dog?subject=${emailSubject}`}>Email files with receipt</a>
        </section>
        <div className="receipt-actions">
          <button className="quiet-button" onClick={() => showScreen("brief")}>View brief</button>
          <button className="text-button" onClick={reset}>Start another discovery</button>
        </div>
      </main>
    );
  }

  if (screen === "send") {
    return <SendScreen draft={draft} questions={visibleQuestions} onBack={() => showScreen("brief")} onReceipt={(next, contact) => {
      setDraft((current) => current ? { ...current, contact, updatedAt: new Date().toISOString() } : current);
      setReceipt(next);
      showScreen("receipt");
    }} />;
  }

  const chapters = [...new Map(visibleQuestions.map((question) => [question.chapter, question.chapterTitle])).entries()];
  const chapterIndex = chapters.findIndex(([id]) => id === currentQuestion?.chapter);
  const answered = visibleQuestions.filter((question) => hasAnswer(draft.answers[question.id])).length;
  const estimate = getRouteEstimate(visibleQuestions);

  return (
    <div className="app-shell">
      <aside className="discovery-rail">
        <div>
          <button className="wordmark" type="button" onClick={() => showScreen("brief")}>
            <strong>pitch.dog</strong><span>Deck Discovery</span>
          </button>
          <p className="rail-route">{routeName(draft)}</p>
        </div>
        <nav aria-label="Discovery chapters">
          {chapters.map(([id, label], index) => {
            const chapterQuestions = visibleQuestions.filter((question) => question.chapter === id);
            const chapterAnswered = chapterQuestions.filter((question) => hasAnswer(draft.answers[question.id])).length;
            const active = index === chapterIndex;
            const firstIndex = visibleQuestions.findIndex((question) => question.chapter === id);
            return (
              <button key={id} data-active={active} aria-current={active ? "step" : undefined} onClick={() => moveTo(firstIndex)}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <span>{label}</span>
                <span>{chapterAnswered}/{chapterQuestions.length}</span>
              </button>
            );
          })}
        </nav>
        <div className="rail-footer">
          <SaveIndicator state={saveState} />
          <button className="text-button" onClick={() => showScreen("brief")}>See brief so far</button>
          <button className="text-button subdued" onClick={reset}>New route</button>
        </div>
      </aside>

      <main className="question-stage" id="main" tabIndex={-1}>
        <header className="question-topline">
          <div>
            <p className="mono-label">CHAPTER {String(chapterIndex + 1).padStart(2, "0")} · {currentQuestion?.chapterTitle.toUpperCase()}</p>
            <p>{currentQuestion?.chapterNote}</p>
          </div>
          <div className="mobile-save">
            <SaveIndicator state={saveState} />
            <button className="text-button" type="button" onClick={() => showScreen("brief")}>Brief so far</button>
          </div>
          <p className="question-count">{answered}/{visibleQuestions.length} answered · about {estimate} min total</p>
        </header>

        {resumeNotice ? <div className="resume-notice" role="status"><span>{resumeNotice}</span><button onClick={() => setResumeNotice(undefined)} aria-label="Dismiss">×</button></div> : null}

        {saveState === "unavailable" ? (
          <div className="storage-warning" role="alert">
            <strong>This browser cannot save drafts reliably.</strong>
            <span>Keep this tab open and use “See brief so far” to download a copy.</span>
          </div>
        ) : null}

        {currentQuestion ? (
          <section className="question-card" key={currentQuestion.id}>
            <div className="question-heading">
              <span className="question-position">{String(boundedQuestionIndex + 1).padStart(2, "0")}</span>
              <div>
                <h1 ref={questionHeadingRef} tabIndex={-1}>{currentQuestion.label}</h1>
                {currentQuestion.whyAsked ? <p className="why-asked">{currentQuestion.whyAsked}</p> : null}
                <p className="requirement">{currentQuestion.required ? "Needed for this route" : "Optional, if useful"} · {currentQuestion.uncertaintyAllowed ? "Unknown is allowed" : "Choose the closest honest answer"}</p>
              </div>
            </div>

            <AnswerField question={currentQuestion} value={draft.answers[currentQuestion.id]} error={fieldError} onChange={setAnswer} />

            <footer className="question-actions">
              <button className="quiet-button" disabled={boundedQuestionIndex === 0} onClick={() => moveTo(boundedQuestionIndex - 1)}>Back</button>
              {!currentQuestion.required ? (
                <button
                  className="text-button"
                  onClick={() => {
                    if (boundedQuestionIndex === visibleQuestions.length - 1) showScreen("brief");
                    else moveTo(boundedQuestionIndex + 1);
                  }}
                >
                  Skip for now
                </button>
              ) : <span />}
              <button className="ink-button" onClick={nextQuestion}>{boundedQuestionIndex === visibleQuestions.length - 1 ? "Build my brief" : "Continue"}</button>
            </footer>
          </section>
        ) : null}
      </main>
    </div>
  );
}

type SendProps = {
  draft: Draft;
  questions: ReturnType<typeof getVisibleQuestions>;
  onBack: () => void;
  onReceipt: (receipt: Receipt, contact: Contact) => void;
};

function SendScreen({ draft, questions, onBack, onReceipt }: SendProps) {
  const [name, setName] = useState(draft.contact.name);
  const [email, setEmail] = useState(draft.contact.email);
  const [phone, setPhone] = useState(draft.contact.phone ?? "");
  const [company, setCompany] = useState(draft.contact.company ?? "");
  const [emailTouched, setEmailTouched] = useState(false);
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState<"idle" | "sending" | "error">("idle");
  const [error, setError] = useState<string>();
  const missing = questions.filter((question) => question.required && !hasAnswer(draft.answers[question.id]));
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const contactInvalid = !name.trim() || !emailValid;

  async function send() {
    setEmailTouched(true);
    if (missing.length || contactInvalid || !consent) return;
    setStatus("sending");
    setError(undefined);
    const allowed = new Set(questions.map((question) => question.id));
    const answers = Object.fromEntries(Object.entries(draft.answers).filter(([id]) => allowed.has(id)));
    const content: SubmissionContent = {
      submissionId: draft.draftId,
      revision: 1,
      schemaVersion: draft.schemaVersion,
      mode: draft.mode,
      vertical: draft.vertical,
      service: draft.service,
      answers,
      startedAt: draft.startedAt,
      contact: {
        name: name.trim(),
        email: email.trim(),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(company.trim() ? { company: company.trim() } : {}),
      },
    };
    try {
      const request = await makeSubmissionRequest(content);
      const receipt = await submitOrQueue(request);
      onReceipt(receipt, content.contact);
    } catch (caught) {
      const message = caught instanceof ApiError ? caught.message : "Submission could not be completed.";
      setError(`${message} Nothing has been cleared from this device. Reconnect and we will retry with the same submission ID.`);
      setStatus("error");
    }
  }

  return (
    <main className="send-screen" id="main">
      <p className="mono-label">EXPLICIT HANDOFF</p>
      <h1>Send this brief to pitch.dog?</h1>
      <p className="send-intro">
        Until you press the final button, {draft.mode === "public" ? "your public self-study stays on this device" : "your private draft remains a draft"}. Files are separate and optional.
      </p>

      {missing.length ? (
        <section className="missing-panel" role="alert">
          <h2>{missing.length} needed answer{missing.length === 1 ? " is" : "s are"} still open.</h2>
          <ul>{missing.slice(0, 8).map((question) => <li key={question.id}>{question.shortLabel}</li>)}</ul>
          <button className="quiet-button" onClick={onBack}>Return to brief</button>
        </section>
      ) : null}

      <div className="contact-grid">
        <label><span>Name</span><input value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" required /></label>
        <label>
          <span>Email <small>we send your answer copy here</small></span>
          <input
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            onBlur={() => setEmailTouched(true)}
            type="email"
            autoComplete="email"
            required
            aria-label="Email"
            aria-invalid={emailTouched && !emailValid}
            aria-describedby="send-email-note"
          />
          <small className="input-note" id="send-email-note" data-error={emailTouched && !emailValid}>
            {emailTouched && !emailValid ? "Use a complete email, like name@example.com." : "Check this carefully; this is where your full copy goes."}
          </small>
        </label>
        <label><span>Phone <small>optional</small></span><input value={phone} onChange={(event) => setPhone(event.target.value)} type="tel" autoComplete="tel" /></label>
        <label><span>Company / project home <small>optional</small></span><input value={company} onChange={(event) => setCompany(event.target.value)} autoComplete="organization" /></label>
      </div>

      <label className="consent-row">
        <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} />
        <span>I choose to send these answers to pitch.dog and receive a copy at the email above. They will be stored for this project and reviewed for deletion or archive after the project plus 12 months. I can email hello@pitch.dog to request deletion.</span>
      </label>

      {error ? <div className="send-error" role="alert">{error}</div> : null}

      <div className="send-actions">
        <button className="quiet-button" onClick={onBack}>Not yet</button>
        <button className="ink-button" disabled={Boolean(missing.length || !consent || status === "sending")} onClick={send}>
          {status === "sending" ? "Protecting two copies…" : "Send and get receipt"}
        </button>
      </div>
    </main>
  );
}

export default App;
